import type React from 'react';
import { type FC, useMemo } from 'react';
import { useTheme } from 'styled-components';
import type { ThemeConfig } from 'antd/lib';
import { EnmonThemeProvider, ThemeModeProvider } from '@enmon-monorepo/ui';
import { THEME } from '@enmon/commons';

interface AppAntConfigProviderProps {
  children: React.ReactNode;
}

/**
 * Passed to `ThemeModeProvider` as `enabled`, not just wrapped around the button: a live provider
 * would still resolve a stored or OS preference and repaint the antd layer, with no control to
 * undo it. Disabled, it reads nothing and reports light.
 */
export const IS_THEME_SWITCHER_ENABLED = import.meta.env.VITE_THEME_SWITCHER_ENABLED === 'true';

const AntConfig: FC<AppAntConfigProviderProps> = ({ children }) => {
  const theme = useTheme();

  // Token stays inside the callback: in the component body it was a new object every render, so
  // depending on it made this memo never hit.
  const base = useMemo<ThemeConfig>(() => {
    const token = {
      borderRadius: 2,
      // Font properties
      fontFamily: THEME.typography.fontFamily.poppins,
      fontWeightStrong: 500,
      // Colors
      colorPrimary: theme.color.theme.primary,
      colorLink: theme.color.theme.primary,
      // Focus state outline
      lineWidthFocus: 0,
    } satisfies ThemeConfig['token'];

    return {
      token,
      hashed: false,
      components: {
        Select: {
          optionActiveBg: theme.color.semantic.primary.background.hoverWeak,
        },
        Tag: {
          borderRadius: 4,
        },
      },
    };
  }, [theme]);

  return <EnmonThemeProvider base={base}>{children}</EnmonThemeProvider>;
};

/**
 * The mode provider sits inside this component rather than at the app entry point, so nothing above
 * has to know the theme is switchable. Render `<ThemeSwitcher />` anywhere below it.
 */
export const AppAntConfigProvider: FC<AppAntConfigProviderProps> = ({ children }) => {
  return (
    <ThemeModeProvider enabled={IS_THEME_SWITCHER_ENABLED}>
      <AntConfig>{children}</AntConfig>
    </ThemeModeProvider>
  );
};
